import React, { useEffect, useMemo } from 'react'
import { AuthorisedUser, CrudActions, EntityType, SpreadsheetMapping, SpreadsheetMappingType } from 'api'
import { Locale, Translation } from 'locale'
import { isEqual } from 'lodash'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { cloneMapping, getDefaultMapping, removeIdsFromMapping } from 'view/shared/upload/model'
import { createSortedSpreadsheetMappingsSelector } from 'selectors/spreadsheetMappingSelectors'
import { isNotNil } from 'utils/predicate'
import { downloadFile } from 'utils'
import { SplitButtonType } from 'view/components/menus'
import { SpreadsheetMappingHelper } from 'model'

import {
  SpreadsheetParseMappingActionsButton,
  SpreadsheetParseMappingContainer,
  SpreadsheetParseMappingSelectbox
} from './SpreadsheetParseMappingSelectorStyles'
import { SpreadsheetMappingUploadDialog } from './SpreadsheetMappingUploadDialog'
import { spreadsheetMappingSortByLastSetAsDefaultPredicate } from './utils'

interface DispatchProps {
  onDeleteSpreadsheetMapping: (mappingId: string) => unknown
  onSave: typeof CrudActions.spreadsheetMappingUpsert
}

interface ConnectedProps {
  getSpreadsheetMapping: (mappingId: string) => SpreadsheetMapping | null
  authorisedUser: AuthorisedUser
  spreadsheetMappings: SpreadsheetMapping[]
}

export type MappingDialogGetter = (title: string, mapping: SpreadsheetMapping) => JSX.Element | undefined

interface SpreadsheetParseMappingSelectorComponentProps extends ModalConsumerProps {
  getMappingModal: MappingDialogGetter
  selectedSpreadsheetMapping: SpreadsheetMapping
  spreadsheetMappingType: SpreadsheetMappingType
  onSpreadsheetMappingSelected: (mapping: SpreadsheetMapping) => void
  canDownloadUploadMapping?: boolean
}

const mapDispatchToProps: DispatchProps = {
  onDeleteSpreadsheetMapping: CrudActions.spreadsheetMappingDelete,
  onSave: CrudActions.spreadsheetMappingUpsert
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector(),
  getSpreadsheetMapping: createEntityGetterSelector(EntityType.SPREADSHEET_MAPPING),
  spreadsheetMappings: createSortedSpreadsheetMappingsSelector(
    (_, ownProps: SpreadsheetParseMappingSelectorComponentProps) => ownProps.spreadsheetMappingType
  )
})

export type SpreadsheetParseMappingSelectorProps = SpreadsheetParseMappingSelectorComponentProps & ConnectedProps & DispatchProps

export const SpreadsheetParseMappingSelectorComponent: React.FC<SpreadsheetParseMappingSelectorProps> = ({
  authorisedUser,
  getSpreadsheetMapping,
  getMappingModal,
  onDeleteSpreadsheetMapping,
  onSpreadsheetMappingSelected,
  onSave,
  selectedSpreadsheetMapping,
  showDialog,
  showModal,
  spreadsheetMappings,
  spreadsheetMappingType,
  canDownloadUploadMapping = true
}) => {
  const defaultSelectedMapping = useMemo(() => {
    const [mostRecentDefault] = spreadsheetMappings.sort(spreadsheetMappingSortByLastSetAsDefaultPredicate)
    return mostRecentDefault
  }, [spreadsheetMappings])

  useEffect(() => {
    if (defaultSelectedMapping) {
      onSpreadsheetMappingSelected(defaultSelectedMapping)
    }
  }, [defaultSelectedMapping])

  const getSafeSelectedMapping = () => selectedSpreadsheetMapping ?? getDefaultMapping(spreadsheetMappingType)

  const handleUpdateMapping = (spreadsheetMappingId: string) => {
    onSpreadsheetMappingSelected(getSpreadsheetMapping(spreadsheetMappingId) ?? getDefaultMapping(spreadsheetMappingType))
  }

  const handleNewMapping = () => {
    showMappingDialog(cloneMapping(getSafeSelectedMapping(), authorisedUser.id), Locale.translate(Translation.TC_CREATE_MAPPING))
  }

  const handleEditMapping = () => showMappingDialog(getSafeSelectedMapping(), Locale.translate(Translation.TC_UPDATE_MAPPING))

  const handleDeleteMapping = async () => {
    const mapping = getSafeSelectedMapping()

    const result = await showDialog({
      type: NotificationType.Confirm,
      title: Translation.TC_PROMPT_TITLE_DELETE_SPREADSHEET_MAPPING,
      message: Translation.SC_PROMPT_MSG_DELETE_SPREADSHEET_MAPPING,
      messageArgs: { name: mapping.name },
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO
    })

    if (result === Translation.TC_BUTTON_YES) {
      onDeleteSpreadsheetMapping(mapping.id)
      onSpreadsheetMappingSelected(getSafeSelectedMapping())
    }
  }

  const handleDownload = () => {
    if (selectedSpreadsheetMapping) {
      downloadFile(
        JSON.stringify(removeIdsFromMapping(selectedSpreadsheetMapping)),
        'text/json',
        `${SpreadsheetMappingHelper.getName(selectedSpreadsheetMapping)}.json`
      )
    }
  }

  const handleUpload = async () => {
    await showModal(<SpreadsheetMappingUploadDialog spreadsheetMappingType={SpreadsheetMappingType.STAGING} />)
  }

  const showMappingDialog = async (mapping: SpreadsheetMapping, title: string) => {
    const modal = getMappingModal(title, mapping)

    if (modal) {
      const spreadsheetMapping = await showModal(modal)
      if (isNotNil(spreadsheetMapping)) {
        onSpreadsheetMappingSelected(spreadsheetMapping)
      }
    }
  }

  const handleSetAsDefault = () => {
    onSave({ ...selectedSpreadsheetMapping, lastSetAsDefault: new Date() })
  }

  const mappingIsEqualToDefault = (mapping: SpreadsheetMapping) => isEqual(mapping, getDefaultMapping(spreadsheetMappingType))

  return (
    <SpreadsheetParseMappingContainer>
      <SpreadsheetParseMappingSelectbox
        getOptionText={m =>
          `${SpreadsheetMappingHelper.getName(m)}${
            m === defaultSelectedMapping ? ` (${Locale.translate(Translation.TC_UPLOAD_DEFAULT)})` : ''
          }`
        }
        dataTestId="SpreadsheetMapping-Selector"
        label={Locale.translate(Translation.SC_SAVED_MAPPINGS)}
        placeholder={Locale.translate(Translation.SC_SYSTEM_MAPPING)}
        value={selectedSpreadsheetMapping?.id}
        canClear={true}
        onUpdate={handleUpdateMapping}
        mappingType={spreadsheetMappingType}
      />

      <SpreadsheetParseMappingActionsButton
        dataTestId="SpreadsheetMapping"
        actions={[
          {
            label: Locale.translate(Translation.TC_BUTTON_UPDATE),
            onClick: handleEditMapping,
            type: SplitButtonType.Primary,
            enabled: !mappingIsEqualToDefault(selectedSpreadsheetMapping)
          },
          {
            label: Locale.translate(Translation.TC_BUTTON_CREATE_NEW),
            onClick: handleNewMapping,
            type: SplitButtonType.Primary
          },
          {
            label: Locale.translate(Translation.TC_BUTTON_DELETE),
            onClick: handleDeleteMapping,
            enabled: !mappingIsEqualToDefault(selectedSpreadsheetMapping)
          },
          {
            label: Locale.translate(Translation.TC_BUTTON_DOWNLOAD),
            onClick: handleDownload,
            enabled: !mappingIsEqualToDefault(selectedSpreadsheetMapping) && canDownloadUploadMapping
          },
          {
            label: Locale.translate(Translation.TC_BUTTON_UPLOAD),
            onClick: handleUpload,
            enabled: canDownloadUploadMapping
          },
          {
            label: Locale.translate(Translation.SC_BUTTON_SET_AS_DEFAULT),
            onClick: handleSetAsDefault,
            type: SplitButtonType.Primary,
            enabled: !mappingIsEqualToDefault(selectedSpreadsheetMapping) && selectedSpreadsheetMapping !== defaultSelectedMapping
          }
        ]}
        maxButtons={1}
      />
    </SpreadsheetParseMappingContainer>
  )
}

export const SpreadsheetParseMappingSelector = withModal(
  connect(mapStateToProps, mapDispatchToProps)(SpreadsheetParseMappingSelectorComponent)
)
